import * as React from "react"

import { cn } from "@/lib/utils"

/*
   Position-based pager: "41–60 of 312" answers "how much is left" better
   than "page 3 of 16" alone. Both live in the bar; the range is louder.

   Changing page size re-anchors on the first row currently visible —
   otherwise switching from 25 to 100 while on page 9 of 9 strands the
   reader on a page that no longer exists. This is why the pager owns the
   paging math.
*/

export interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (p: number) => void
  /** Offering sizes without this is decoration; omit it to hide the control. */
  onPageSizeChange?: (n: number) => void
  pageSizes?: number[]
  className?: string
}

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(function Pagination(
  { page, pageSize, total, onPageChange, onPageSizeChange, pageSizes = [10, 25, 50], className },
  ref,
) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-wrap items-center justify-between gap-3.5 px-4 py-2.5",
        className,
      )}
    >
      <div className="flex items-center gap-3.5">
        <span className="font-id text-micro tabular-nums text-text-3">
          {from}–{to} of {total}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-micro text-text-3">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                const firstVisible = (page - 1) * pageSize
                onPageSizeChange(next)
                onPageChange(Math.floor(firstVisible / next) + 1)
              }}
              className="rounded-xs border border-border bg-surface px-1 py-0.5 font-id text-micro text-text-2"
            >
              {pageSizes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-1">
        <PageBtn
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1)
          }}
          label="Previous page"
        >
          Previous
        </PageBtn>
        <span className="px-2 font-id text-micro tabular-nums text-text-2">
          {page} / {pages}
        </span>
        <PageBtn
          disabled={page >= pages}
          onClick={() => {
            onPageChange(page + 1)
          }}
          label="Next page"
        >
          Next
        </PageBtn>
      </div>
    </div>
  )
})

function PageBtn({
  children,
  disabled,
  onClick,
  label,
}: {
  children: React.ReactNode
  disabled?: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-6.5 rounded-md border border-border bg-surface px-2.5",
        "text-caption text-text-2 transition-colors duration-120 ease-out",
        "hover:border-border-strong hover:text-text",
        "disabled:pointer-events-none disabled:opacity-40",
      )}
    >
      {children}
    </button>
  )
}
